import React, { createContext, useContext, useState } from 'react';
import { WidgetContainerContext } from '@/core/widgets/contexts/widget-container-context';
import { WidgetContainer } from '@/core/widgets/model/widget-container';

export type ViewMode = 'normal' | 'headline' | 'readMore';

export const DEFAULT_TEXT_SCALE = 1;
export const MIN_TEXT_SCALE = 0.7;
export const MAX_TEXT_SCALE = 1.5;

interface WidgetViewModeContextType {
  viewMode: ViewMode | null;
  setViewMode: (value: ViewMode) => void;
  textScale: number;
  setTextScale: (value: number) => void;
}

interface WidgetViewModeProviderProps {
  children: React.ReactNode;
  widgetId?: string;
}

const WidgetViewModeContext = createContext<WidgetViewModeContextType | undefined>(undefined);

function storageKey(id: string) {
  return `widget-view-mode-${id}`;
}

function textScaleStorageKey(id: string) {
  return `widget-text-scale-${id}`;
}

function clampTextScale(value: number): number {
  if (Number.isNaN(value)) return DEFAULT_TEXT_SCALE;
  return Math.min(MAX_TEXT_SCALE, Math.max(MIN_TEXT_SCALE, value));
}

export function WidgetViewModeProvider({
  children,
  widgetId: widgetIdProp,
}: WidgetViewModeProviderProps) {
  const { widgetId: contextWidgetId } = useContext<WidgetContainer>(WidgetContainerContext);
  const widgetId = widgetIdProp ?? contextWidgetId;

  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    if (!widgetId) return 'normal';
    return (localStorage.getItem(storageKey(widgetId)) as ViewMode) ?? 'normal';
  });

  const [textScale, setTextScaleState] = useState<number>(() => {
    if (!widgetId) return DEFAULT_TEXT_SCALE;
    const stored = localStorage.getItem(textScaleStorageKey(widgetId));
    return stored ? clampTextScale(parseFloat(stored)) : DEFAULT_TEXT_SCALE;
  });

  const setViewMode = (value: ViewMode) => {
    setViewModeState(value);
    if (widgetId) localStorage.setItem(storageKey(widgetId), value);
  };

  const setTextScale = (value: number) => {
    const clamped = clampTextScale(value);
    setTextScaleState(clamped);
    if (widgetId) localStorage.setItem(textScaleStorageKey(widgetId), `${clamped}`);
  };

  return (
    <WidgetViewModeContext.Provider value={{ viewMode, setViewMode, textScale, setTextScale }}>
      {children}
    </WidgetViewModeContext.Provider>
  );
}

export function useWidgetViewMode(): WidgetViewModeContextType {
  const context = useContext(WidgetViewModeContext);
  if (context === undefined) {
    return {
      viewMode: null,
      setViewMode: () => {},
      textScale: DEFAULT_TEXT_SCALE,
      setTextScale: () => {},
    };
  }
  return context;
}
